import React, { useCallback, useEffect, useState } from 'react';
import Spinner from './ui/Spinner';
import { adminApi } from '../config/axios';
import { useGlobal } from '../contexts/GlobalContext';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const unwrap = (res) => res?.data?.data ?? null;
const MODE_AR = { off: 'متوقّف', shadow: 'تجريبي (يسجّل ولا يرسل)', live: 'مباشر' };

/** "2026-10-29T09:30:00+00:00" -> "2026-10-29 12:30" in Amman time (Latin digits). */
export function ammanTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Amman', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(d).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

const DELIVERY_AR = { delivered: 'وصلت إلى صندوق المستلم (حسب مزوّد البريد)', bounced: 'ارتدّت الرسالة (لم تصل)', complained: 'صنّفها المستلم بريداً مزعجاً', delivery_delayed: 'التسليم متأخر',
  sent: 'أُرسلت من المزوّد وما زال التسليم قيد الانتظار', queued: 'في طابور المزوّد', canceled: 'أُلغيت', opened: 'فُتحت', clicked: 'فُتحت وتم النقر على رابط فيها' };

/** One line about the LAST email attempt, exactly what the mail provider answered (never a success that was not proven). */
export function emailLine(r, label) {
  if (!r) return { tone: 'muted', text: `${label}: لم يُرسَل بعد.` };
  const t = ammanTime(r.at);
  if (!r.ok) return { tone: 'bad', text: `${label}: فشل الإرسال (${t}) — ${r.reason_ar || 'سبب غير معروف'}` };
  let text = `${label}: قبِلَ مزوّد البريد الرسالة (${t})${r.message_id ? ` — المعرّف ${r.message_id}` : ''}.`;
  if (r.delivery_status) text += ` حالة التسليم: ${DELIVERY_AR[r.delivery_status] ?? r.delivery_status}.`;
  else text += ' قبول المزوّد ليس تأكيداً للوصول: تحقّق من صندوق الوارد.';
  return { tone: r.delivery_status === 'bounced' || r.delivery_status === 'complained' ? 'bad' : 'ok', text };
}

const arError = (err) => {
  const m = err?.response?.data?.errors?.email?.[0] ?? err?.response?.data?.message ?? '';
  if (/already on the list/i.test(m)) return 'هذا البريد مضاف مسبقاً.';
  if (/at most/i.test(m)) return 'الحد الأقصى 20 مستلماً.';
  if (/valid email|must be a valid/i.test(m)) return 'عنوان البريد غير صالح.';
  if (/at least one recipient/i.test(m)) return 'أضف مستلماً واحداً على الأقل أولاً.';
  return m || 'تعذّر تنفيذ الطلب.';
};

/**
 * «التنبيهات» -- the internal ops alerts (phase 1, team only): who receives the 09:00 daily summary email (stored in the database, editable here, nothing in env),
 * a "send test email" button, and a read-only Telegram status (which group, last successful send) with a "send test message" button.
 */
export default function OpsAlertsCard() {
  const { showToast } = useGlobal();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [data, setData] = useState(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const apply = useCallback((d) => setData(d), []);

  useEffect(() => {
    adminApi.get('/ops-alerts').then((res) => apply(unwrap(res))).catch(() => showToast('error', 'خطأ', 'تعذّر تحميل إعدادات التنبيهات.')).finally(() => setLoading(false));
  // showToast comes from context and may change identity every render: it must not retrigger the load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apply]);

  const add = async () => {
    const v = email.trim();
    if (!EMAIL_RE.test(v)) { setError('أدخل عنوان بريد صالحاً.'); return; }
    setError('');
    setBusy('add');
    try {
      const res = await adminApi.post('/ops-alerts/recipients', { email: v });
      apply(unwrap(res));
      setEmail('');
      showToast('success', 'تمت الإضافة', `${v.toLowerCase()} سيستلم الملخص اليومي.`);
    } catch (err) { setError(arError(err)); } finally { setBusy(null); }
  };

  const remove = async (addr) => {
    setBusy('rm:' + addr);
    try {
      const res = await adminApi.delete('/ops-alerts/recipients', { data: { email: addr } });
      apply(unwrap(res));
      showToast('success', 'تم الحذف', `${addr} لن يستلم الملخص بعد الآن.`);
    } catch (err) { showToast('error', 'خطأ', arError(err)); } finally { setBusy(null); }
  };

  const testEmail = async () => {
    setBusy('email');
    try {
      const res = await adminApi.post('/ops-alerts/test-email');
      const d = unwrap(res);
      showToast('success', 'قبِلَ مزوّد البريد الرسالة', `إلى: ${(d?.sent_to ?? []).join('، ')}${d?.message_id ? ` — المعرّف ${d.message_id}` : ''}`);
    } catch (err) { showToast('error', 'فشل إرسال البريد', arError(err)); } finally {
      try { apply(unwrap(await adminApi.get('/ops-alerts'))); } catch { /* the line below keeps the previous state */ }
      setBusy(null);
    }
  };

  const testTelegram = async () => {
    setBusy('tg');
    try {
      const res = await adminApi.post('/ops-alerts/test-telegram');
      const d = unwrap(res);
      showToast('success', 'أُرسلت رسالة التجربة إلى تيليغرام', `المجموعة: ${d?.group_title ?? ''} — رقم الرسالة ${d?.message_id ?? ''}`);
      const fresh = await adminApi.get('/ops-alerts');
      apply(unwrap(fresh));
    } catch (err) { showToast('error', 'تعذّر الإرسال', err?.response?.data?.message ?? 'تعذّر الإرسال.'); } finally { setBusy(null); }
  };

  if (loading) return <div className="card-padded flex justify-center py-10"><Spinner size="lg" /></div>;
  if (!data) return <div className="card-padded text-text-muted text-sm" dir="rtl" data-testid="ops-alerts-card">تعذّر تحميل التنبيهات.</div>;

  const tg = data.telegram ?? {};
  const last = ammanTime(tg.last_success_at);
  const recipients = data.recipients ?? [];

  return (
    <div className="card-padded" dir="rtl" data-testid="ops-alerts-card">
      <div className="mb-5">
        <h2 className="text-base font-semibold text-text-main">التنبيهات</h2>
        <p className="text-text-muted text-sm mt-0.5">
          تنبيهات داخلية للفريق فقط (لا يصل أي شيء إلى العملاء): رسائل فورية على تيليغرام، وملخص يومي الساعة 09:00 بتوقيت عمّان على تيليغرام وعلى البريد أدناه.
        </p>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between gap-3 mb-2">
          <h3 className="text-sm font-semibold text-text-main">مستلمو الملخص اليومي بالبريد</h3>
          <button type="button" className="btn-secondary" onClick={testEmail} disabled={busy !== null || recipients.length === 0} data-testid="test-email">
            {busy === 'email' ? <Spinner size="sm" /> : 'إرسال بريد تجريبي إلى القائمة'}
          </button>
        </div>
        <div className="flex gap-2 items-start">
          <div className="flex-1">
            <input type="email" dir="ltr" value={email} onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }} placeholder="name@example.com"
              className={`input-base ${error ? 'border-danger/60' : ''}`} data-testid="email-input" />
            {error && <p className="text-danger text-xs mt-1" data-testid="email-error">{error}</p>}
          </div>
          <button type="button" className="btn-primary flex-shrink-0" onClick={add} disabled={busy !== null} data-testid="add">
            {busy === 'add' ? <Spinner size="sm" /> : 'إضافة'}
          </button>
        </div>
        {recipients.length === 0 ? (
          <p className="text-text-muted text-sm mt-3" data-testid="empty">لا يوجد مستلمون — يصل الملخص اليومي عبر تيليغرام فقط.</p>
        ) : (
          <ul className="mt-3 divide-y divide-white/[0.06] rounded-lg border border-white/[0.06]" data-testid="recipients">
            {recipients.map((r) => (
              <li key={r} className="flex items-center justify-between gap-3 px-3 py-2">
                <span dir="ltr" className="text-sm text-text-main" data-testid="recipient">{r}</span>
                <button type="button" className="btn-secondary" onClick={() => remove(r)} disabled={busy !== null} data-testid={`remove-${r}`}>حذف</button>
              </li>
            ))}
          </ul>
        )}
        {(() => {
          const t = emailLine(data.email?.last_test, 'آخر بريد تجريبي');
          const m = emailLine(data.email?.last_summary, 'آخر ملخص يومي بالبريد');
          const cls = (x) => (x.tone === 'bad' ? 'text-danger' : x.tone === 'ok' ? 'text-text-main' : 'text-text-muted');
          return (
            <div className="mt-3 space-y-1 text-sm" data-testid="email-status">
              <p className={cls(t)} data-testid="email-last-test">{t.text}</p>
              <p className={cls(m)} data-testid="email-last-summary">{m.text}</p>
            </div>
          );
        })()}
        <p className="text-text-muted text-xs mt-2">يمكن إضافة حتى {data.max_recipients ?? 20} عنواناً. التغيير يسري فوراً دون إعادة نشر.</p>
      </div>

      <div>
        <div className="flex items-center justify-between gap-3 mb-2">
          <h3 className="text-sm font-semibold text-text-main">تيليغرام</h3>
          <button type="button" className="btn-secondary" onClick={testTelegram} disabled={busy !== null || !tg.configured} data-testid="test-telegram">
            {busy === 'tg' ? <Spinner size="sm" /> : 'إرسال رسالة تجربة إلى تيليغرام'}
          </button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div><dt className="label">المجموعة</dt>
            <dd className="text-text-main" data-testid="tg-group">{tg.group_title ? tg.group_title : (tg.configured ? 'تعذّر قراءة المجموعة' : 'غير مضبوط')}</dd></div>
          <div><dt className="label">آخر إرسال ناجح (بتوقيت عمّان)</dt>
            <dd className="text-text-main" dir="ltr" data-testid="tg-last">{last ?? 'لم تُرسل أي رسالة بعد'}</dd></div>
          <div><dt className="label">الوضع</dt>
            <dd className="text-text-main" data-testid="tg-mode">{MODE_AR[data.mode] ?? data.mode}{data.environment && data.environment !== 'PROD' ? ` — بيئة ${data.environment}` : ''}</dd></div>
        </dl>
        {tg.error && <p className="text-danger text-xs mt-2" dir="ltr" data-testid="tg-error">{tg.error}</p>}
      </div>
    </div>
  );
}
